import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPath, useNavigate, useParams } from "react-router-dom";
import { Flex, Skeleton } from "antd";
import { createStyles } from "antd-style";

import { usePublicCompany } from "@av/apps/common-apps/platform/services/companies/public-company";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { jobsRoutes } from "@av/apps/recruitment/ats-common/routes/jobs/jobs.route-config";
import { useProvinces } from "@av/apps/recruitment/ats-common/services/lookup-entities/provinces/provinces";
import { type JobPostFilters } from "@av/apps/recruitment/ats-common/types";
import { useDocumentTitle } from "@av/components/templates/layouts/errors/hooks/useDocumentTitle";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { EmptyJobsState } from "./components/EmptyJobsState";
import { FiltersBar } from "./components/FiltersBar";
import { JobDetailSection } from "./components/JobDetailSection";
import { JobList } from "./components/JobList";
import { FilterSearchParamMapper } from "./hooks/search-params-mapper";
import { jobBoardEntryState, useJobBoardFilterParams } from "./hooks/useJobBoardFilterParams";
import { PublicNotFoundPage } from "../../components/errors/PublicNotFoundPage";
import { useCanonicalUrl } from "../../hooks/useCanonicalUrl";
import { useNoIndex } from "../../hooks/useNoIndex";
import { usePublicFetchJobPosts } from "../../services/public-job-posts/public-job-posts";

const useStyles = createStyles(({ token, css }) => ({
  layout: css`
    width: 100%;
    display: grid;
    grid-template-columns: 460px 1fr;
    gap: ${token.marginLG}px;
    align-items: flex-start;
    @media (max-width: ${token.screenLG}px) {
      grid-template-columns: 1fr;
    }
  `,
}));

function JobsPage() {
  const { styles } = useStyles();
  const { t } = useTranslation();
  const { locale } = useLocaleStore();
  const navigate = useNavigate();
  const testIdPrefix = "public-jobs";
  const { companyId, jobPostId: jobPostIdFromUrl } = useParams();

  const {
    filters,
    filtersActive,
    restoredFromSavedState,
    searchParamsString,
    setPage,
    setFilters,
  } = useJobBoardFilterParams(companyId);
  const isPageKeptRef = useRef(false);
  // The saved query replaces a bare URL before the posts finish loading; retain its origin once.
  const restoredPagePendingRef = useRef(restoredFromSavedState);

  useEffect(() => {
    if (restoredFromSavedState) {
      restoredPagePendingRef.current = true;
    }
  }, [restoredFromSavedState]);

  const { data: allJobs = [], isLoading: isLoadingAllJobs } = usePublicFetchJobPosts(companyId, {});
  const {
    data: fetchedJobPosts = [],
    pageSize,
    paginatedJobs = [],
    isLoading: isLoadingJobs,
    currentPage = 1,
    visibleTotal = 0,
    findPageNumberForJob,
    findFirstJobInPage,
  } = usePublicFetchJobPosts(companyId, filters);

  const subdivisionOptions = Array.from(
    new Set(allJobs.map((job) => job.subdivisionCode).filter(Boolean))
  ) as string[];

  const { data: publicCompany } = usePublicCompany(companyId);

  const { dataDropDown: provincesDropdown = [] } = useProvinces({
    provinceCodes: subdivisionOptions,
  });

  let selectedJob = null;

  if (!isLoadingJobs && fetchedJobPosts.length > 0) {
    selectedJob = fetchedJobPosts.find((j) => j.jobPostId === jobPostIdFromUrl) || null;
  }

  const isNotFound =
    !companyId || (!isLoadingJobs && !!jobPostIdFromUrl && !selectedJob && !filtersActive);
  const companyName = publicCompany?.commonName;
  let documentTitle: string | undefined;

  if (!isNotFound && companyName) {
    documentTitle = selectedJob
      ? t("recruitment.jobs.documentTitle_jobPost", {
          jobTitle: getLocalizedJobPostContent(selectedJob.jobTitles, locale),
          company: companyName,
        })
      : t("recruitment.jobs.documentTitle_jobBoard", { company: companyName });
  }

  useDocumentTitle(documentTitle);
  const routedJob = allJobs.find((job) => job.jobPostId === jobPostIdFromUrl);
  const isRoutedJobUnindexable =
    !isLoadingAllJobs && !!jobPostIdFromUrl && routedJob?.postStatus !== "POSTED";

  useNoIndex(!isNotFound && isRoutedJobUnindexable);
  useCanonicalUrl(!isRoutedJobUnindexable);

  // Auto-navigate to the first job when none is specified in the URL
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(visibleTotal / pageSize));

    if (!isLoadingJobs && !jobPostIdFromUrl && filters.page && filters.page > lastPage) {
      setPage(restoredPagePendingRef.current ? lastPage : 1);
      restoredPagePendingRef.current = false;

      return;
    }

    if (!isLoadingJobs && fetchedJobPosts.length > 0 && !jobPostIdFromUrl) {
      restoredPagePendingRef.current = false;
      const firstVisibleJob = findFirstJobInPage(currentPage) ?? fetchedJobPosts[0];

      if (!firstVisibleJob || firstVisibleJob.postStatus !== "POSTED") {
        return;
      }

      navigate(
        {
          pathname: jobsRoutes.home.view.path({ pathParam: firstVisibleJob.jobPostId }),
          search: searchParamsString ? `?${searchParamsString}` : "",
        },
        { state: jobBoardEntryState("board") }
      );
    }
  }, [
    currentPage,
    fetchedJobPosts,
    findFirstJobInPage,
    filters.page,
    isLoadingJobs,
    jobPostIdFromUrl,
    navigate,
    pageSize,
    searchParamsString,
    setPage,
    visibleTotal,
  ]);

  // Select page with the job in the job list
  useEffect(() => {
    if (!isLoadingJobs && jobPostIdFromUrl) {
      restoredPagePendingRef.current = false;
    }

    if (selectedJob) {
      const jobPage: number = findPageNumberForJob(selectedJob.jobPostId);

      if (jobPage >= 1 && jobPage !== currentPage) {
        if (!isPageKeptRef.current) {
          setPage(jobPage);

          return;
        }

        // A cleared filter keeps the page, so select a job on that page instead of moving to the
        // page of the selected job.
        isPageKeptRef.current = false;

        const firstJobInPage = findFirstJobInPage(currentPage);

        if (firstJobInPage) {
          navigate(
            {
              pathname: jobsRoutes.home.view.path({ pathParam: firstJobInPage.jobPostId }),
              search: `?${searchParamsString}`,
            },
            { replace: true, state: jobBoardEntryState("board") }
          );
        }
      } else if (filters.page && filters.page !== currentPage) {
        setPage(currentPage);
      }
    }
  }, [
    currentPage,
    filters.page,
    findFirstJobInPage,
    findPageNumberForJob,
    isLoadingJobs,
    jobPostIdFromUrl,
    navigate,
    searchParamsString,
    selectedJob,
    setPage,
  ]);

  const changeFilters = (newFilters: JobPostFilters) => {
    isPageKeptRef.current = setFilters(newFilters);
  };

  const pageLocation = (page: number) => {
    const job = findFirstJobInPage(page);

    if (!job) {
      return undefined;
    }

    const pageSearchParams = new FilterSearchParamMapper(searchParamsString);

    pageSearchParams.setPage(page);

    return {
      pathname: jobsRoutes.home.view.path({ pathParam: job.jobPostId }),
      search: `?${pageSearchParams.urlSearchParams}`,
    };
  };

  const changePage = (page: number) => {
    const location = pageLocation(page);

    if (location) {
      navigate(location, { state: jobBoardEntryState("board") });
    }
  };

  const pageHref = (page: number) => {
    const location = pageLocation(page);

    return location && createPath(location);
  };

  if (isNotFound) {
    return <PublicNotFoundPage />;
  }

  const hasNoPostedJobs = fetchedJobPosts.every((p) => p.postStatus !== "POSTED");
  const shouldShowEmptyState = !isLoadingJobs && !selectedJob && !filtersActive && hasNoPostedJobs;

  if (shouldShowEmptyState) {
    return <EmptyJobsState />;
  }

  return (
    <Flex vertical style={{ width: "inherit" }}>
      {!isLoadingAllJobs && (
        <FiltersBar
          filters={filters}
          onFiltersChange={changeFilters}
          locationOptions={provincesDropdown}
          testIdPrefix={testIdPrefix}
        />
      )}

      <Skeleton active loading={isLoadingJobs}>
        <div className={styles.layout}>
          <JobList
            visibleJobs={paginatedJobs}
            allJobs={allJobs}
            selectedJobId={selectedJob?.jobPostId}
            total={visibleTotal}
            pageSize={pageSize}
            page={currentPage}
            onPageChange={changePage}
            pageHref={pageHref}
            testIdPrefix={testIdPrefix}
          />

          <JobDetailSection
            job={selectedJob}
            testIdPrefix={testIdPrefix}
            hasNoJobPostsMatchingFilters={filtersActive && hasNoPostedJobs}
            clearFilters={() => changeFilters({})}
          />
        </div>
      </Skeleton>
    </Flex>
  );
}

export { JobsPage };
